import React from 'react';
import PropTypes from 'prop-types';
import LaddaButton from 'react-ladda';
import styled from '@emotion/styled';
import { Button } from 'react-bootstrap';
import { useMedia } from 'react-use-media';

import colours from '~/utils/colours';
import StepContext from './StepContext';
import useEventListener from '~/react-hooks/use-event-listener';

const Container = styled.div`
  display: flex;
  align-items: center;

  button {
    margin-right: 5px;
    z-index: 0;
  }

  .ladda-label {
    color: white;
  }

  span {
    color: ${colours.greyMediumDark};
  }
`;

interface HelpTextProps {
  isComplete: boolean;
  isCurrent: boolean;
  helpText: string;
}

function HelpText({ isComplete, isCurrent, helpText = 'Press ENTER' }: HelpTextProps) {
  const isMobile = useMedia({
    maxWidth: 768,
  });

  if (!isComplete || isMobile || !isCurrent) {
    return null;
  }

  return <span>{helpText}</span>;
}

HelpText.propTypes = {
  isComplete: PropTypes.bool,
  isCurrent: PropTypes.bool,
  helpText: PropTypes.string,
};

interface ActionButtonProps {
  className?: string;
  desktopText?: string;
  mobileText?: string;
  disableHotkey?: boolean;
  disabled?: boolean;
  helpText?: string;
  onClick?: (e?: React.MouseEvent<Button, MouseEvent>) => void;
  canGoToNextStep?: boolean;
  isComplete?: boolean;
}

const ActionButton = React.forwardRef<Button, ActionButtonProps>(
  (
    {
      className,
      desktopText = 'Ok',
      mobileText = 'Done',
      disableHotkey = false,
      disabled,
      helpText,
      onClick,
      canGoToNextStep = true,
      ...props
    },
    ref
  ) => {
    const { isCurrent, isComplete, nextStep, stepRef } =
      React.useContext(StepContext);

    const isActive = React.useMemo(() => {
      if (typeof props.isComplete === 'boolean') {
        return props.isComplete;
      }

      return isComplete && !disabled;
    }, [isComplete, disabled, props.isComplete]);
    const isMobile = useMedia({
      maxWidth: 768,
    });

    function handleClick(e?: React.MouseEvent<Button, MouseEvent>) {
      if (isActive) {
        if (onClick) onClick(e);

        if (canGoToNextStep) {
          nextStep();
        }
      }
    }

    function simulateClick() {
      handleClick();
    }

    function handleKeyDown(evt: React.KeyboardEvent<HTMLElement>) {
      if (disableHotkey) return;

      const keyCode = evt.keyCode;
      /** Codes for Enter and numpad Enter */
      if (keyCode == 13 || keyCode == 176) {
        simulateClick();
      }
    }

    useEventListener<HTMLElement, 'keydown'>(
      'keydown',
      (evt) => handleKeyDown(evt as React.KeyboardEvent<HTMLElement>),
      stepRef
    );

    return (
      <Container className={className}>
        <Button
          ref={ref}
          componentClass={LaddaButton}
          onClick={(e) => handleClick(e)}
          bsStyle={isActive ? 'primary' : 'default'}
          disabled={!isActive}
          {...props}
        >
          {isMobile ? mobileText : desktopText}
        </Button>
        {!disableHotkey && (
          <HelpText
            helpText={helpText}
            isCurrent={isCurrent}
            isComplete={isActive}
          />
        )}
      </Container>
    );
  }
);

ActionButton.displayName = 'ActionButton';

ActionButton.propTypes = {
  isComplete: PropTypes.bool,
  helpText: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  desktopText: PropTypes.string,
  mobileText: PropTypes.string,
  disableHotkey: PropTypes.bool,
  onClick: PropTypes.func,
  canGoToNextStep: PropTypes.bool,
};

const StyledActionButton = styled(ActionButton)`
  margin-top: 5px;
`;

export default React.memo(StyledActionButton);
